import React, { useCallback } from 'react';
import { defineMessages } from 'react-intl-next';
import { graphql } from 'react-relay';

import { Box, Stack } from '@atlaskit/primitives/compiled';
import Form from '@atlaskit/form';
import { token } from '@atlaskit/tokens';
import { cssMap } from '@atlaskit/css';

import { RefetchAudioButton, setCustomization, getCustomization } from '@atlassian/ai-audio';

import { useSessionData } from '@confluence/session-data/entry-points/useSessionData';
import { fg } from '@confluence/feature-gating';
import { useMutationInterop } from '@confluence/apollo-relay-interop';

import type {
	CustomizePanelUpdateAudioPreferenceMutation$data,
	CustomizePanelUpdateAudioPreferenceMutation$variables,
	ConfluenceLength,
	ConfluenceTone,
} from './__generated__/CustomizePanelUpdateAudioPreferenceMutation.graphql';
import { DurationSelector } from './DurationSelector';
import { ToneSelector } from './ToneSelector';

const i18n = defineMessages({
	refetchAudioButtonText: {
		id: 'audio.audio-popup.customize-tab.refetch-audio-button',
		defaultMessage: 'Re-start briefing',
		description: 'Text for a button that refetches audio and plays it when clicked',
	},
});

const styles = cssMap({
	formWrapper: {
		paddingInline: token('space.075'),
	},
});

interface CustomizationFormValues {
	length: ConfluenceLength | null;
	tone: ConfluenceTone | null;
	playbackSpeed: number | null;
}

export const CustomizePanel = () => {
	const customization = getCustomization();
	const { cloudId } = useSessionData();

	const defaultValues: CustomizationFormValues = {
		length: customization.length as ConfluenceLength,
		tone: customization.tone as ConfluenceTone,
		playbackSpeed: customization.playbackSpeed || 1.0,
	};

	const [commitMutation] = useMutationInterop<
		CustomizePanelUpdateAudioPreferenceMutation$data,
		CustomizePanelUpdateAudioPreferenceMutation$variables
	>(graphql`
		mutation CustomizePanelUpdateAudioPreferenceMutation(
			$input: ConfluenceUpdateAudioPreferenceInput!
			$cloudId: ID!
		) {
			confluence_updateAudioPreference(cloudId: $cloudId, input: $input) {
				success
				preference {
					playbackSpeed
					length
					tone
				}
			}
		}
	`);

	const handlePreferenceUpdate = useCallback(
		async (
			updatedPreferences: Partial<{
				length: ConfluenceLength;
				tone: ConfluenceTone;
				playbackSpeed: number;
			}>,
		) => {
			const currentCustomization = getCustomization();

			const input = {
				length: updatedPreferences.length ?? (currentCustomization.length as ConfluenceLength),
				tone: updatedPreferences.tone ?? (currentCustomization.tone as ConfluenceTone),
				playbackSpeed:
					updatedPreferences.playbackSpeed ?? currentCustomization.playbackSpeed ?? 1.0,
			};

			try {
				const result = await commitMutation({
					variables: {
						input,
						cloudId,
					},
				});

				if (
					result.data?.confluence_updateAudioPreference?.success &&
					result.data.confluence_updateAudioPreference.preference
				) {
					setCustomization({
						...currentCustomization,
						...result.data.confluence_updateAudioPreference.preference,
					});
				} else if (result.data?.confluence_updateAudioPreference?.success === false) {
					// TODO: CCI-13829 Handle error appropriately (show user notification)
				}
			} catch (_error) {
				// TODO: CCI-13829 Handle error appropriately (show user notification)
			}
		},
		[commitMutation, cloudId],
	);

	return (
		<Box xcss={styles.formWrapper}>
			{fg('platform-design_system_team-form_conversion') ? (
				<Form<CustomizationFormValues> onSubmit={() => {}}>
					<Stack space="space.100">
						<ToneSelector
							onPreferenceUpdate={handlePreferenceUpdate}
							defaultValue={defaultValues.tone}
						/>
						<DurationSelector
							onPreferenceUpdate={handlePreferenceUpdate}
							defaultValue={defaultValues.length}
						/>
						<RefetchAudioButton buttonText={i18n.refetchAudioButtonText} source="customizePanel" />
					</Stack>
				</Form>
			) : (
				<Form<CustomizationFormValues> onSubmit={() => {}}>
					{({ formProps }) => (
						<form {...formProps}>
							<Stack space="space.100">
								<ToneSelector
									onPreferenceUpdate={handlePreferenceUpdate}
									defaultValue={defaultValues.tone}
								/>
								<DurationSelector
									onPreferenceUpdate={handlePreferenceUpdate}
									defaultValue={defaultValues.length}
								/>
								<RefetchAudioButton
									buttonText={i18n.refetchAudioButtonText}
									source="customizePanel"
								/>
							</Stack>
						</form>
					)}
				</Form>
			)}
		</Box>
	);
};
